import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { RouteComponent } from '@app/route/route.component';
import { HeaderService } from '@app/service/header/header.service';
import {
  StateControlParameters,
  StateService,
  ZaverProduct,
} from '@app/service/state/state.service';
import { UserService } from '@app/service/user-service/user.service';
import { Subscription } from 'rxjs';
import { delay, repeatWhen, takeWhile } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import QRCodeStyling from 'qr-code-styling';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AppDeepLinkService } from '@app/service/app-deeplink/app-deep-link.service';
import { AuthStatusDto } from '@app/service/auth/dto/auth-status.dto';
import { PaymentRequestDto } from '@app/model/payment-request/payment-request.dto';
import { LoggingService } from '@app/service/logging-service/logging.service';

@Component({
  selector: 'authenticate',
  templateUrl: './authenticate.component.html',
  styleUrls: ['./authenticate.component.scss', '../../page-shared.css'],
})
export class AuthenticateComponent
  extends RouteComponent
  implements OnInit, OnDestroy
{
  @ViewChild('canvas') canvas: ElementRef;

  readonly scanQrCodeStatusMessage =
    'Starta BankID-appen i din mobil och tryck på ikonen för QR-kod. Rikta kameran mot QR-koden på den här sidan.';
  readonly beforeBankIdAutoStartStatusMessage = 'Vänta...';
  readonly bankIdStatusOutstandingTransaction = 'OUTSTANDING_TRANSACTION';
  private pollingSubscription: Subscription;
  private pollingPaused = false;
  private lastAutoStartToken: string = '';
  private authRefId: string = '';
  private qrCode = <QRCodeStyling>{};
  private qrCodeCurrentRetries: number = 0;
  private initService;
  private completeService;
  private paymentRequest: PaymentRequestDto;

  public autoStart: boolean = false;
  public navigationInProgress = false;
  public authError: boolean = false;
  public loading: boolean = true;
  public showQrCode: boolean = false;
  public authInProgress: boolean = false;
  public authCompleted: boolean = false; // Successful authentication
  public bankidAuthFailed: boolean = false;
  public isMobile: boolean = false;
  public bankIdStatusMessage: string = '';

  constructor(
    private userService: UserService,
    protected stateService: StateService,
    protected headerService: HeaderService,
    protected router: Router,
    protected route: ActivatedRoute,
    private deviceDetectorService: DeviceDetectorService,
    private deepLinkService: AppDeepLinkService,
    private loggingService: LoggingService
  ) {
    super(router, route, stateService, headerService);
  }

  async ngOnInit(): Promise<void> {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    const path: string = this.route.routeConfig.path;
    const currentPageName = this.stateService.getStepFromUrl(path);
    const isAuthenticate = currentPageName === 'AUTHENTICATE';

    if (
      this.state.zaverProduct !== ZaverProduct.TOKENIZED_ONBOARDING &&
      this.state.zaverProduct !== ZaverProduct.PRECHECK
    ) {
      try {
        this.paymentRequest = await this.stateService.fetchPaymentRequest(
          this.stateService.getToken()
        );
      } catch (e) {
        this.loggingService.warn('Failed to fetch payment request for product ' + this.state.zaverProduct);
      }
    }

    this.initService = isAuthenticate
      ? this.userService.initAuthenticate()
      : this.userService.initSign();

    this.completeService = isAuthenticate
      ? this.userService.completeAuthenticate()
      : this.userService.completeSign();

    this.isMobile = this.deviceDetectorService.isMobile();
    this.showQrCode = !this.isMobile;
    const authId = this.deepLinkService.getCurrentAuthRefId();

    if (authId) {
      const isOngoingAuth = await this.userService.checkOngoingAuthentication();
      this.loggingService.warn(
        `[SE-Auth] token=${this.stateService.getToken()} checkOngoingAuthentication=${isOngoingAuth} — ${isOngoingAuth ? 'resuming poll (iOS new-tab case)' : 'starting fresh BankID auth'}`
      );
      if (isOngoingAuth) {
        // this should only happen if a browser on iOS opens a new tab instead of reusing the old one
        // won't happen for Chrome and Firefox
        // shouldn't happen for Safari, although it's not documented and may change
        this.startPolling();
      } else {
        this.startBankidAuth();
      }
    } else {
      this.startBankidAuth();
    }
    this.loading = false;
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.stopPolling();
  }

  private handleVisibilityChange = (): void => {
    this.loggingService.warn(
      `[SE-Auth]  token=${this.stateService.getToken()} visibilitychange — state=${document.visibilityState} authInProgress=${this.authInProgress} authCompleted=${this.authCompleted} navigationInProgress=${this.navigationInProgress} pollingPaused=${this.pollingPaused} authRefId=${this.authRefId}`
    );
    if (document.visibilityState === 'visible') {
      this.pollingPaused = false;
      if (this.authInProgress && !this.authCompleted && (!this.pollingSubscription || this.pollingSubscription.closed)) {
        this.startPolling();
      }
    } else if (!this.showQrCode) {
      // Pause without unsubscribing — unsubscribing aborts the in-flight XHR, but the server may
      // have already processed /complete and registered the user.
      this.pollingPaused = true;
    }
  };

  private async startBankidAuth(): Promise<void> {
    this.bankIdStatusMessage = this.showQrCode
      ? this.scanQrCodeStatusMessage
      : this.beforeBankIdAutoStartStatusMessage;

    this.authInProgress = true;
    this.authCompleted = false;
    this.bankidAuthFailed = false;
    this.pollingPaused = false;
    const authStatusDto: AuthStatusDto = await this.initService.toPromise();

    this.authRefId = authStatusDto.id;
    this.lastAutoStartToken = authStatusDto.autoStartToken;
    this.loggingService.warn(`[SE-Auth] token=${this.stateService.getToken()} BankID auth initialized — authRefId=${this.authRefId}`);

    if (authStatusDto.active) {
      this.startPolling();
      if (this.isMobile && !this.showQrCode) {
        if (this.autoStart) {
          this.openBankIdApp();
        }
      } else {
        this.updateQrCode(authStatusDto.qrCodeData);
      }
    }
  }

  public isStartBankIdButtonEnabled(): boolean {
    return this.bankIdStatusMessage === this.beforeBankIdAutoStartStatusMessage;
  }

  private startPolling(): void {
    if (document.visibilityState === 'hidden' && !this.showQrCode) return;

    this.loggingService.warn(
      `[SE-Auth] token=${this.stateService.getToken()} startPolling — authRefId=${this.authRefId} visibility=${document.visibilityState}`
    );

    this.pollingPaused = false;

    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }

    this.pollingSubscription = this.completeService
      .pipe(
        // Inner takeWhile stops the repeat loop (not the in-flight request) when paused.
        repeatWhen((obs) => obs.pipe(delay(1000), takeWhile(() => !this.pollingPaused))),
        takeWhile((res: AuthStatusDto) => res.active && !res.authenticated, true)
      )
      .subscribe((authStatusDto: AuthStatusDto) => {
        const { status, message, active, authenticated, qrCodeData } = authStatusDto;

        const displayStandardMessage =
          this.showQrCode &&
          (status === null ||
            status === this.bankIdStatusOutstandingTransaction);

        this.bankIdStatusMessage = displayStandardMessage
          ? this.scanQrCodeStatusMessage
          : message;

        this.showQrCode && this.updateQrCode(qrCodeData);

        if (!active && !authenticated) {
          this.handleUnsuccessfulAuth();
        } else if (authenticated) {
          this.loggingService.warn(
            `[SE-Auth] token=${this.stateService.getToken()} poll returned authenticated=true — authRefId=${this.authRefId} navigationInProgress=${this.navigationInProgress}`
          );
          this.handleSuccessfulAuth();
        } else if (active) {
          // this.handleInProgress(auth);
        }
      });
  }

  public showQrCodeClick(): void {
    this.resetQrCodeComponent();
    this.stopPolling();
    this.navigationInProgress = false; // explicit: user is intentionally restarting the flow
    this.startBankidAuth();
  }

  private resetQrCodeComponent(): void {
    this.qrCode._options && this.qrCode.update({ data: '' });
    this.qrCode = <QRCodeStyling>{};
    this.showQrCode = true;
  }

  private handleUnsuccessfulAuth(): void {
    const qrCodeMaxRetries = 3;
    this.qrCodeCurrentRetries++;

    if (this.qrCodeCurrentRetries < qrCodeMaxRetries) {
      this.startBankidAuth();
    } else {
      this.bankidAuthFailed = true;
      this.authInProgress = false;
    }
  }

  public openBankIdApp(): void {
    this.deepLinkService.startBankId(
      this.lastAutoStartToken,
      this.authRefId
    );
  }

  public autoStartAppClick(): void {
    this.showQrCode = false;
    this.autoStart = true;

    this.authInProgress
      ? this.openBankIdApp()
      : this.startBankidAuth().then(() => this.openBankIdApp());
  }

  private updateQrCode(data?: string): void {
    if (!data) {
      return;
    }

    if (this.qrCode._options) {
      this.qrCode.update({ data: data });
      return;
    }

    this.qrCode = new QRCodeStyling({
      width: 180,
      height: 180,
      type: 'svg',
      margin: 0,
      data: data,
      cornersSquareOptions: {
        type: 'extra-rounded',
      },
      cornersDotOptions: {
        type: 'dot',
      },
      qrOptions: {
        errorCorrectionLevel: 'L',
      },
      backgroundOptions: {
        color: 'rgba(0,0,0,0)',
      },
    });

    this.qrCode.append(this.canvas.nativeElement);
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }

    this.authInProgress = false;
    // navigationInProgress is intentionally NOT reset here: stopPolling() is called both from
    // handleSuccessfulAuth() (before navigation) and from ngOnDestroy (triggered by the router
    // during navigation). Resetting it in ngOnDestroy would defeat the re-entry guard.
  }

  async handleSuccessfulAuth(): Promise<void> {
    if (this.navigationInProgress) {
      this.loggingService.warn(
        `[SE-Auth]token=${this.stateService.getToken()} handleSuccessfulAuth suppressed — navigationInProgress already true (authRefId=${this.authRefId})`
      );
      return;
    }

    // Stop polling immediately before yielding at navigate(), so the repeatWhen timer cannot
    // fire a second subscription and trigger a duplicate handleSuccessfulAuth call.
    this.stopPolling();
    this.loggingService.warn(
      `[SE-Auth] token=${this.stateService.getToken()} handleSuccessfulAuth — navigating (authRefId=${this.authRefId})`
    );
    this.navigationInProgress = true;
    this.authCompleted = true;
    // authInProgress already set to false by stopPolling()
    this.loading = true;
    await this.navigate();
  }

  /**
   * Back nagivation only allowed once the state is loaded, and if no payment method was pre-selected.
   */
  backNavigationAllowed(): boolean {
    const checkoutProducts = [
      ZaverProduct.ECOM_CHECKOUT,
      ZaverProduct.IN_STORE_CHECKOUT,
    ];
    return (
      !this.loading &&
      checkoutProducts.includes(this.state.zaverProduct) &&
      !this.paymentRequest?.specificPaymentMethodType
    );
  }

  async reroute(): Promise<void> {
    const path: string = this.route.routeConfig.path;

    const controlParams: StateControlParameters = {
      currentPageName: this.stateService.getStepFromUrl(path),
      token: this.stateService.getToken(),
    };

    this.state = await this.stateService.reset(controlParams);
    this.stopPolling();
    const route = this.stateService.getRouteFromState(this.state);
    this.router.navigate([route], { queryParamsHandling: 'preserve' });
  }
}
